// src/sections/BrokersSection.jsx
import React, { useEffect, useMemo, useState } from "react";
import Slider from "react-slick";
import { FaPhoneAlt, FaMapMarkerAlt, FaArrowRight } from "react-icons/fa";
import { Link } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "../styles/slick-custom.css";

import app from "../firebase";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot,
} from "firebase/firestore";

const db = getFirestore(app);

const BrokersSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to public brokers list
  useEffect(() => {
    const q = query(collection(db, "brokers"), orderBy("name"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => {
          const v = d.data() || {};
          const city = v.city || "";
          const state = v.state || "";
          const location = [city, state].filter(Boolean).join(", ");
          // keep tel digits only
          const phoneDigits = (v.phone || "").toString().replace(/\D/g, "").slice(-10);
          return {
            id: d.id,
            name: v.name || "Broker",
            location,
            phoneDigits,                         // for tel: link/formatting
            phoneRaw: v.phone || "",             // original text, just in case
            image: v.profilePic || v.profilePicture || v.photoURL || "",
            email: v.email || "",
          };
        });
        setItems(list);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  // Duplicate to keep continuous scrolling buttery smooth
  const slides = useMemo(() => {
    if (!items.length) return [];
    return [...items, ...items, ...items];
  }, [items]);

  const toUS = (digits) => {
    if (!digits || digits.length < 10) return "";
    const d = digits.padStart(10, "0").slice(-10);
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
  };

  const showCount = useMemo(
    () => Math.max(1, Math.min(5, items.length || 1)),
    [items.length]
  );

  const settings = useMemo(
    () => ({
      infinite: true,
      slidesToShow: showCount,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 0, // continuous
      speed: 20000,     // smooth glide; adjust 15000–25000 to taste
      cssEase: "linear",
      waitForAnimate: false,
      edgeFriction: 0,
      pauseOnHover: false,
      pauseOnFocus: false,
      pauseOnDotsHover: false,
      swipe: false,
      touchMove: false,
      draggable: false,
      arrows: false,
      dots: false,
      responsive: [
        { breakpoint: 1280, settings: { slidesToShow: Math.max(1, Math.min(4, items.length || 1)) } },
        { breakpoint: 1024, settings: { slidesToShow: Math.max(1, Math.min(3, items.length || 1)) } },
        { breakpoint: 768,  settings: { slidesToShow: Math.max(1, Math.min(2, items.length || 1)) } },
        { breakpoint: 640,  settings: { slidesToShow: 1 } },
      ],
    }),
    [items.length, showCount]
  );

  // Simple skeleton while loading
  if (loading) {
    return (
      <div className="brokers-slider relative">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-44 bg-white rounded-2xl border border-[#002b5c]/10 shadow-sm p-6 animate-pulse">
              <div className="h-16 w-16 rounded-full bg-gray-200 mb-4" />
              <div className="h-4 bg-gray-200 w-2/3 mb-2 rounded" />
              <div className="h-3 bg-gray-200 w-1/2 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!slides.length) {
    // No brokers in DB
    return null;
  }

  return (
    <div className="brokers-slider relative">
      {/* Edge fades (hidden on mobile) */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-transparent hidden sm:block" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent hidden sm:block" />

      <Slider {...settings}>
        {slides.map((b, i) => {
          const telDigits = b.phoneDigits;
          const formatted = toUS(telDigits);
          const initials = b.name
            .split(" ")
            .filter(Boolean)
            .map((n) => n[0])
            .join("")
            .slice(0, 2)
            .toUpperCase();

          return (
            <div key={`${b.id || b.name}-${i}`} className="broker-slide">
              <article
                className="group h-full bg-white rounded-2xl border border-[#002b5c]/12
                           shadow-sm transition-colors duration-200
                           hover:shadow-md hover:border-[#002b5c]/20"
              >
                {/* Header */}
                <div className="p-6 md:p-7 text-center">
                  {b.image ? (
                    <img
                      src={b.image}
                      alt={b.name}
                      className="mx-auto mb-4 h-16 w-16 md:h-20 md:w-20 rounded-full object-cover ring-2 ring-[#002b5c]/15"
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className="mx-auto mb-4 h-16 w-16 md:h-20 md:w-20 rounded-full
                                 bg-gradient-to-br from-[#0a2b52] to-[#001b35] text-white
                                 flex items-center justify-center text-xl md:text-2xl font-bold
                                 ring-2 ring-[#002b5c]/10"
                    >
                      {initials}
                    </div>
                  )}

                  <h3 className="text-lg md:text-xl font-bold text-[#002b5c] leading-snug">
                    {b.name}
                  </h3>

                  {b.location && (
                    <div className="mt-1 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs bg-[#002b5c]/5 text-[#002b5c] font-medium">
                      <FaMapMarkerAlt className="text-[#e31c3d]" />
                      <span>{b.location}</span>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="h-px bg-[#002b5c]/10 mx-6 md:mx-7" />

                {/* Actions */}
                <div className="p-6 md:p-7 pt-4 md:pt-5 flex flex-col items-stretch gap-2.5 md:gap-3">
                  {telDigits ? (
                    <a
                      href={`tel:+1${telDigits}`}
                      aria-label={`Call ${b.name}${formatted ? ` at ${formatted}` : ""}`}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full
                                 border border-[#e31c3d]/30 px-5 py-3 text-sm text-[#e31c3d] font-semibold
                                 hover:bg-[#e31c3d]/10 transition-colors whitespace-nowrap"
                    >
                      <FaPhoneAlt className="shrink-0" />
                      <span className="whitespace-nowrap">{formatted}</span>
                    </a>
                  ) : (
                    <div
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full
                                 border border-gray-200 px-5 py-3 text-sm text-gray-500 font-semibold cursor-not-allowed"
                      title="Phone number unavailable"
                    >
                      <FaPhoneAlt className="shrink-0" />
                      <span className="whitespace-nowrap">Phone unavailable</span>
                    </div>
                  )}

                  <Link
                    to="/brokers"
                    aria-label={`Learn more about ${b.name}`}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full
                               bg-[#002b5c] text-white px-5 py-3 text-sm font-semibold
                               hover:bg-[#001b35] transition-colors whitespace-nowrap"
                  >
                    Learn More <FaArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </article>
            </div>
          );
        })}
      </Slider>
    </div>
  );
};

export default BrokersSection;
